import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

interface Props {
  seriesIndex?: any;
  dataPointIndex?: any;
  w?: any;
  currentYear: string;
  selectedMonth?: string | null;
  onClose?: () => void;
  isSRBM?: boolean;
}

const CustomMonthTooltip: React.FC<Props> = ({
  seriesIndex,
  dataPointIndex,
  w,
  currentYear,
  selectedMonth,
  onClose,
  isSRBM,
}) => {
  const { t } = useTranslation();
  if (!w || seriesIndex === undefined || dataPointIndex === undefined) {
    return null;
  }
  const seriesData = w.config.series[seriesIndex]?.data[dataPointIndex] || {};
  const monthAbbr = w.config.series[seriesIndex].name;

  const yValue = monthAbbr;
  const xValue = seriesData.x;
  const date = dayjs(`${currentYear}-${selectedMonth}-${yValue}`).format('ddd');
  const month = dayjs(`${currentYear}-${selectedMonth}-${yValue}`).format('MMM');

  const captureCount = Math.round(seriesData.captureCount ?? 0);
  const nonCaptureCount = Math.round(seriesData.nonCaptureCount ?? 0);
  const eatenBaitAmount = seriesData.eaten_bait ?? 0;
  const isUpcoming = seriesData.y === -1000000;
  const hasNoActivity = captureCount === 0 && nonCaptureCount === 0;

  const captureSignalsMCT = seriesData.times.filter(
    (signal: string) => signal.includes('APPROVED_MICE') && signal.includes('mct'),
  );

  const captureSignalsSMST = seriesData.times.filter(
    (signal: string) => signal.includes('APPROVED_MICE') && signal.includes('smst'),
  );
  const captureSignalsSRST = seriesData.times.filter(
    (signal: string) => signal.includes('APPROVED_MICE') && signal.includes('srst'),
  );
  const captureSignalsSRBM = seriesData.times.filter(
    (signal: string) => signal.includes('CONSUMPTION') && signal.includes('srbm'),
  );
  const nonCaptureSignalsMCT = seriesData.times.filter(
    (signal: string) => !signal.includes('APPROVED_MICE') && signal.includes('mct'),
  );
  const nonCaptureSignalsSMST = seriesData.times.filter(
    (signal: string) => !signal.includes('APPROVED_MICE') && signal.includes('smst'),
  );
  const nonCaptureSignalsSRST = seriesData.times.filter(
    (signal: string) => !signal.includes('APPROVED_MICE') && signal.includes('srst'),
  );
  const noncaptureSignalsSRBM = seriesData.times.filter(
    (signal: string) => !signal.includes('CONSUMPTION') && signal.includes('srbm'),
  );
  const roundedCountNumbers = (count: number) => {
    let enterOrBait = '';
    if (count < 0.01) {
      enterOrBait = '0';
    } else {
      enterOrBait = count.toFixed(2);
    }
    return enterOrBait;
  };
  return (
    <div className="popover" style={{ width: onClose ? '310px' : '300px', maxWidth: '100%' }}>
      <div className="popover-header m-0 py-1 bg-light">
        <div className="d-flex align-items-center mx-2 justify-content-between gap-3">
          <div className="d-flex">
            {selectedMonth ? (
              <>
                {`${date} ${yValue}th `}
                <br />
                {`at ${xValue.split(':')[0]}:00 - ${xValue.split(':')[0]}:59`}
              </>
            ) : (
              <>{`${yValue} ${xValue}`}</>
            )}
          </div>

          {!isUpcoming && (
            <div className="d-flex justify-content-between flex-column" style={{ alignItems: 'flex-start' }}>
              {isSRBM ? (
                <div>
                  {' '}
                  <div className="m-0 small">
                    {t('MCTDetail.heatmap.titleSRBMWithOut')}: {nonCaptureCount}
                  </div>
                  <div className="m-0 small">
                    {t('MCTDetail.heatmap.titleSRBMWith')}: {captureCount}
                  </div>
                  <div className="m-0 small">
                    {t('MCTDetail.heatmap.titleEatinBait')}: {roundedCountNumbers(eatenBaitAmount)}g
                  </div>
                </div>
              ) : (
                <div>
                  <h6 className={`m-0 ${isSRBM ? 'small' : ''} `}>
                    {isSRBM ? t('MCTDetail.heatmap.titleSRBMWithOut') : t('MCTDetail.heatmap.captures')} :{' '}
                    {captureCount}
                  </h6>
                  <h6 className={`m-0 ${isSRBM ? 'small' : ''} `}>
                    {isSRBM ? t('MCTDetail.heatmap.titleSRBMWith') : t('MCTDetail.heatmap.no_captures')}:{' '}
                    {nonCaptureCount}
                  </h6>
                </div>
              )}
            </div>
          )}

          {onClose && <button type="button" className="btn-close" onClick={onClose} />}
        </div>
      </div>
      <div className="popover-body text-center">
        {hasNoActivity ? (
          <h6>
            {t('MCTDetail.heatmap.no_activity')} {t('MCTDetail.heatmap.on')}{' '}
            {seriesData.x.includes(':') ? `${month} ${monthAbbr}` : seriesData.x}
            {t('MCTDetail.heatmap.th')}{' '}
            {seriesData.x.includes(':') ? t('MCTDetail.heatmap.at') : t('MCTDetail.heatmap.of')}{' '}
            {!seriesData.x.includes(':') ? monthAbbr : `${seriesData.x}-${seriesData.x.replace(':00', ':59')}`}
          </h6>
        ) : (
          <div
            className="d-flex flex-column px-3"
            style={{
              alignItems: 'flex-start',
              textAlign: 'left',
              width: '100%',
              maxHeight: onClose ? '300px' : 'auto',
              overflowY: 'auto',
            }}
          >
            {captureSignalsMCT.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('ChartsAnalitics.heatmap_chart.mctCapture')}</h6>
                {captureSignalsMCT.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`capture-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {captureSignalsSRST.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('ChartsAnalitics.heatmap_chart.srstCapture')}</h6>
                {captureSignalsSRST.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`capture-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {captureSignalsSMST.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('ChartsAnalitics.heatmap_chart.smstCapture')}</h6>
                {captureSignalsSMST.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`capture-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {captureSignalsSRBM.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('MCTDetail.heatmap.titleSRBMWith')}</h6>
                {captureSignalsSRBM.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`capture-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}g</div>
                    </div>
                  );
                })}
              </div>
            )}
            {nonCaptureSignalsMCT.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('ChartsAnalitics.heatmap_chart.mctNonCapture')}</h6>
                {nonCaptureSignalsMCT.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`non-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {nonCaptureSignalsSMST.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('ChartsAnalitics.heatmap_chart.smstNonCapture')}</h6>
                {nonCaptureSignalsSMST.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`non-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {nonCaptureSignalsSRST.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('ChartsAnalitics.heatmap_chart.srstNonCapture')}</h6>
                {nonCaptureSignalsSRST.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const enter = parts[parts.length - 2];
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`non-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}</div>

                      {enter === 'R' && (
                        <div className="d-flex align-items-center mr-2">
                          <i className="ri-arrow-right-line" />
                          <div>R</div>
                        </div>
                      )}

                      {enter === 'L' && (
                        <div className="d-flex align-items-center mr-2">
                          <div>L</div>
                          <i className="ri-arrow-left-line" />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {noncaptureSignalsSRBM.length > 0 && (
              <div className="d-flex flex-column gap-0 w-100 mt-2">
                <h6>{t('MCTDetail.heatmap.titleSRBMWithOut')}</h6>
                {noncaptureSignalsSRBM.map((signal: string, index: number) => {
                  const parts = signal.trim().split(' ');
                  const rawDateTime = parts.slice(0, parts.length - 2).join(' ');

                  const dateTime = dayjs(rawDateTime).isValid()
                    ? dayjs(rawDateTime).format('DD-MM-YYYY\xa0\xa0\xa0HH:mm:ss')
                    : rawDateTime;

                  return (
                    <div className="d-flex" key={`non-${index}`} style={{ gap: '6px' }}>
                      <div>{dateTime}g</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomMonthTooltip;
